import { getDefaultMobileConfig } from "@solana/connector/headless";
import { AppProvider, getDefaultConfig } from "@solana/connector/react";
import { APP_NAME, RPC_ENDPOINT } from "@solomon-apps/config";
import React, { useMemo } from "react";

import { SolomonE2EWalletProvider } from "./e2e";
import { WalletModalProvider } from "./WalletModal";

interface WalletProviderProps {
    children: React.ReactNode;
}

/**
 * WalletProvider
 *
 * Root provider for wallet connectivity using Solana Foundation's ConnectorKit.
 * Provides:
 * - AppProvider from ConnectorKit for wallet connection management
 * - WalletModalProvider for programmatic modal control
 * - Mobile Wallet Adapter (MWA) for native Android wallet signing
 *
 * ConnectorKit automatically detects Wallet Standard compliant wallets
 * (Phantom, Solflare, Backpack, etc.) - no manual adapter list needed.
 *
 * Based on official ConnectorKit Next.js example.
 *
 * Note: ConnectionManager is initialized in createApp() before React renders.
 */
export function WalletProvider({
    children
}: WalletProviderProps): React.ReactElement {
    // Get app URL for ConnectorKit config
    const appUrl =
        typeof window !== "undefined"
            ? window.location.origin
            : "http://localhost:3000";

    // ConnectorKit configuration with built-in auto-reconnection
    const connectorConfig = useMemo(
        () =>
            getDefaultConfig({
                appName: APP_NAME,
                appUrl,
                autoConnect: true,
                enableMobile: true,
                clusters: [
                    {
                        id: "solana:mainnet" as const,
                        label: "Mainnet",
                        url: RPC_ENDPOINT
                    }
                ]
            }),
        [appUrl]
    );

    // Mobile Wallet Adapter configuration for native Android wallet signing.
    // Registers MWA via @solana-mobile/wallet-standard-mobile, enabling
    // bottom-sheet signing on Solana Mobile devices (Saga/Seeker).
    const mobile = useMemo(
        () =>
            getDefaultMobileConfig({
                appName: APP_NAME,
                appUrl
            }),
        [appUrl]
    );

    return (
        <AppProvider connectorConfig={connectorConfig} mobile={mobile}>
            <SolomonE2EWalletProvider>
                <WalletModalProvider>{children}</WalletModalProvider>
            </SolomonE2EWalletProvider>
        </AppProvider>
    );
}
